import { useEffect, useState } from "react";

import { ASSISTANT_COMPLETION_EVENT } from "config";

import { AssistantStep, IAssistantHighlight } from "../types";
import { findAssistantTarget } from "../utils";
import {
  findAssistantHighlightTargets,
  getAssistantVisibleHighlight,
} from "./utils";

export function useAssistantWalkthroughTarget(
  step: AssistantStep | undefined,
  onComplete: () => void
) {
  const [highlights, setHighlights] = useState<IAssistantHighlight[]>([]);

  useEffect(() => {
    if (!step) {
      setHighlights([]);
      return;
    }

    let animationFrame = 0;
    let highlightedElements: HTMLElement[] = [];
    let isStepCompleted = false;

    const completeStep = () => {
      if (isStepCompleted) return;

      isStepCompleted = true;
      onComplete();
    };

    const updateHighlight = () => {
      const elements = findAssistantHighlightTargets(step.target);
      const firstElement = elements[0];

      if (firstElement && firstElement !== highlightedElements[0]) {
        firstElement.scrollIntoView({
          block: "center",
          inline: "nearest",
          behavior: "auto",
        });
      }

      if (
        elements.length !== highlightedElements.length ||
        elements.some(
          (element, index) => element !== highlightedElements[index]
        )
      ) {
        resizeObserver.disconnect();
        const ancestors = new Set<HTMLElement>();
        for (const element of elements) {
          for (
            let ancestor: HTMLElement | null = element;
            ancestor;
            ancestor = ancestor.parentElement
          ) {
            ancestors.add(ancestor);
          }
        }
        ancestors.forEach((ancestor) => resizeObserver.observe(ancestor));
        highlightedElements = elements;
      }

      setHighlights(
        elements.flatMap((element) => {
          const highlight = getAssistantVisibleHighlight(element);
          return highlight ? [highlight] : [];
        })
      );

      if (step.completionTarget && findAssistantTarget(step.completionTarget)) {
        completeStep();
      }
    };

    const scheduleUpdate = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(updateHighlight);
    };

    const handleCompletion = (event: Event) => {
      if (
        event instanceof CustomEvent &&
        step.completionEvent === event.detail
      ) {
        completeStep();
      }
    };

    const resizeObserver = new ResizeObserver(scheduleUpdate);
    const observer = new MutationObserver(scheduleUpdate);

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        "data-assistant-target",
        "data-assistant-highlight-group",
        "aria-hidden",
        "inert",
        "hidden",
        "disabled",
        "class",
        "style",
      ],
    });
    window.addEventListener("scroll", scheduleUpdate, true);
    window.addEventListener("resize", scheduleUpdate);
    window.addEventListener(ASSISTANT_COMPLETION_EVENT, handleCompletion);
    scheduleUpdate();

    return () => {
      observer.disconnect();
      resizeObserver.disconnect();
      cancelAnimationFrame(animationFrame);
      window.removeEventListener("scroll", scheduleUpdate, true);
      window.removeEventListener("resize", scheduleUpdate);
      window.removeEventListener(ASSISTANT_COMPLETION_EVENT, handleCompletion);
    };
  }, [step, onComplete]);

  return step ? highlights : [];
}
